/**
 * ChunkReloadBoundary
 *
 * Self-heals stale dynamic-import chunks after a deploy.
 *
 * When the user has an old `index.html` cached (or an SW serving stale assets),
 * route-level lazy() calls reject with a "Failed to fetch dynamically imported
 * module" / "Importing a module script failed" error. Without intervention the
 * outer Suspense waits forever and the splash screen stalls 10–15s before the
 * global ErrorBoundary takes over.
 *
 * This boundary catches the chunk-load error pattern and triggers ONE
 * window.location.reload(), guarded by sessionStorage so we never loop.
 * Any other error is re-thrown so the outer ErrorBoundary still handles it.
 */

import { Component, type ReactNode } from "react";
import { showAppLoader, hideAppLoader } from "@/lib/appLoader";

const RELOAD_FLAG = "chunk_reload_attempted_at";
const RELOAD_TTL_MS = 30_000; // suppress re-reloads for 30s after a self-heal

interface Props {
  children: ReactNode;
}

interface State {
  reloading: boolean;
  fatalError: Error | null;
}

function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message ?? "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    /Loading chunk \d+ failed/i.test(msg) ||
    err.name === "ChunkLoadError"
  );
}

function shouldReload(): boolean {
  try {
    const last = sessionStorage.getItem(RELOAD_FLAG);
    if (!last) return true;
    const lastMs = Number(last);
    if (!Number.isFinite(lastMs)) return true;
    return Date.now() - lastMs > RELOAD_TTL_MS;
  } catch {
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* ignore */
  }
}

export class ChunkReloadBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { reloading: false, fatalError: null };
  }

  static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadError(error) && shouldReload()) {
      markReloaded();
      // Fire reload async — React needs to commit this state first.
      queueMicrotask(() => {
        window.location.reload();
      });
      return { reloading: true, fatalError: null };
    }
    return { reloading: false, fatalError: error };
  }

  componentDidMount() {
    if (this.state.reloading) showAppLoader();
  }

  componentDidUpdate(_: Props, prev: State) {
    if (this.state.reloading && !prev.reloading) showAppLoader();
  }

  componentWillUnmount() {
    if (this.state.reloading) hideAppLoader();
  }

  render() {
    if (this.state.fatalError) {
      // Re-throw to the next boundary up (the global ErrorBoundary).
      throw this.state.fatalError;
    }
    if (this.state.reloading) {
      // The pre-React #app-loader element is re-shown via showAppLoader().
      return null;
    }
    return this.props.children;
  }
}

export default ChunkReloadBoundary;
